import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { withStyles } from '@material-ui/styles';
import ZoomIn from '@material-ui/icons/ZoomIn';
import ZoomOut from '@material-ui/icons/ZoomOut';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import Slider from '@material-ui/core/Slider';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';

import {
	addWidgetsToUpdateWaitingList,
	updateEditingSceneAndSendToPlayer,
	updateEditingSceneDurationAction,
} from '../../../actions/scenarioActions';
import {
	convertTimeToWidgetTime,
	getSecondsFromTimelineTime,
	getTimelineStartDate,
	getTimelineTimeFromSeconds,
	getTimelineTimeFromTimeInput,
} from '../../../services/timeStampService';
import { loadResourcesAfterTheFlowStartedForMedias } from '../../../services/mediaService';
import play_black from '../../../assets/images/play_black.svg';
import pause_controls from '../../../assets/images/pause_controls.svg';
import fast_forward_15 from '../../../assets/images/fast_forward_15.svg';
import fast_reward_15 from '../../../assets/images/fast_reward_15.svg';
import { QPLAYER_IDENTIFIER, WIDGET_TYPES } from '../../../constants/scenarioConstant';
import { showAlert } from '../../../actions/globalActions';
import { messageTypes } from '../../../constants/mediaConstants';
import { isEventMessageComeFromEditorPlayer } from '../SceneEditor/widgetOverlay/widgetInteractionItem/helper';

import './EditorTimeline.scss';

// ── Domain types ───────────────────────────────────────────────────────────────

interface WidgetStyle {
	zIndex?: number;
	[key: string]: unknown;
}

interface WidgetTemplate {
	id: string;
	name?: string;
	type: string;
	style: WidgetStyle;
	settings?: Record<string, unknown>;
}

interface BoxWidget {
	id: string;
	widgetTemplateId: string;
	start?: string;
	end?: string;
}

interface Box {
	boxTemplateId: string;
	widgets: BoxWidget[];
}

interface Layout {
	id?: string;
	type?: string;
	boxes: Box[];
}

interface EditingScene {
	id: string;
	layouts: Layout[];
	widgetTemplates: WidgetTemplate[];
}

interface SceneDuration {
	start: Date | string | null;
	end: Date | string | null;
}

interface RootState {
	scenarioReducer: {
		editingScene: EditingScene | null | undefined;
		timelineChanged: unknown;
		editingSceneDuration: SceneDuration | null;
		scenario: { id?: string };
	};
	session: {
		defaultAccountId: string;
	};
}

interface TimelineItem {
	id: string;
	widgetId: string;
	content: string;
	group: string | number;
	className: string;
	start: Date;
	end: Date;
	isVideoBG: boolean;
	settings?: Record<string, unknown>;
}

interface TimelineGroup {
	id: string;
	value: number | string;
	content: string;
	visible: boolean;
	className?: string;
}

interface ViewRange {
	startMs: number;
	endMs: number;
}

interface DragState {
	active: boolean;
	type: 'move' | 'resize-left' | 'resize-right' | null;
	item: TimelineItem | null;
	startMouseX: number;
	startMouseY: number;
	startItemStartMs: number;
	startItemEndMs: number;
	startGroupId: string | number | null;
	currentGroupId: string | number | null;
	snapCandidates: number[];
	snapThreshold: number;
	contentWidth: number;
	dragAxis: 'horizontal' | 'vertical' | null;
}

export interface EditorTimelineHandle {
	updateZIndexPosition: (_isBringToFront: boolean, _widgetId: string, _templateId: string) => void;
	getHighestZIndexValue: () => number;
}

interface EditorTimelineProps {
	movePlayerToPosition?: (_position: number) => void;
	onPause?: () => void;
	onPlay?: () => void;
	onMute?: () => void;
	onUnMute?: () => void;
	onForward?: () => void;
	onReward?: () => void;
	onDropItem?: (_data: Record<string, unknown>) => void;
	onItemClick?: (_data: { id: string; widgetTemplateId: string; boxId: string; group: string | number }) => void;
	getSceneDuration?: (_fn: () => number) => void;
	editingWidget?: { id: string; widgetTemplateId: string } | null;
	activeLayout?: string;
	accountId?: string;
	sceneEditor?: {
		props: { editingScene: EditingScene | null };
		updateMediaToVideoWidget: (
			_mediaDetail: unknown,
			_tpl: WidgetTemplate | undefined,
			_boxWidget: BoxWidget | undefined
		) => void;
	};
}

// ── Constants ──────────────────────────────────────────────────────────────────

const DEFAULT_BG_ID = '1';
const START_TIME = '00:00:00:000';
const baseClassName = 'editor-timeline';
const itemClassName = `${baseClassName}__item`;
const LABEL_WIDTH = 40;
const ROW_HEIGHT = 39;
const TIMELINE_SNAPPING_SENSITIVE_UNDER_A_MINUTES = 200;
const TIMELINE_SNAPPING_SENSITIVE_FROM_10_MINUTES = 10000;

const colors: Record<string, string> = {
	[WIDGET_TYPES.WIDGET_TYPE_VIDEO]: 'blue',
	[WIDGET_TYPES.WIDGET_TYPE_BUTTON]: 'pink',
	[WIDGET_TYPES.WIDGET_TYPE_TEXT]: 'purple',
	[WIDGET_TYPES.WIDGET_TYPE_ICON]: 'yellow',
	[WIDGET_TYPES.WIDGET_TYPE_IMAGE]: 'green',
	[WIDGET_TYPES.WIDGET_TYPE_HOTSPOT]: 'red',
	[WIDGET_TYPES.WIDGET_TYPE_SHAPE]: 'orange',
};

// ── Pure helpers ───────────────────────────────────────────────────────────────

const getItemClassnames = (type: string): string => {
	const value = colors[type] ?? 'white';
	return `${itemClassName} ${itemClassName}--${value}`;
};

const toMs = (d: Date | string): number => (d instanceof Date ? d : new Date(d)).getTime();

const getWidgetsDuration = (widget: { start: Date | string; end: Date | string }): number =>
	toMs(widget.end) - toMs(widget.start);

const getTickInterval = (durationMs: number): number => {
	const intervals = [500, 1000, 2000, 5000, 10000, 30000, 60000, 120000, 300000, 600000, 1800000, 3600000];
	return intervals.find((d) => durationMs / d <= 12) ?? 3600000;
};

const generateTicks = (viewStartMs: number, viewEndMs: number): number[] => {
	const interval = getTickInterval(viewEndMs - viewStartMs);
	const refMs = getTimelineStartDate().getTime();
	const firstTick = Math.ceil((viewStartMs - refMs) / interval) * interval + refMs;
	const ticks: number[] = [];
	for (let ms = firstTick; ms <= viewEndMs; ms += interval) {
		ticks.push(ms);
	}
	return ticks;
};

const formatMs = (ms: number): string => moment(ms).format('HH:mm:ss');

// ── Styled slider ──────────────────────────────────────────────────────────────

const CustomSlider = withStyles({
	root: { height: 4 },
	thumb: {
		height: 10,
		width: 10,
		backgroundColor: '#fff',
		border: '1px solid currentColor',
		marginTop: -3,
		marginLeft: -6,
		'&:focus, &:hover, &$active': { boxShadow: 'inherit' },
	},
	active: {},
	valueLabel: {
		left: 'auto',
		top: -18,
		'& *': { background: 'transparent', color: '#000' },
	},
	track: { height: 4, borderRadius: 4 },
	rail: { height: 4, borderRadius: 4 },
})(Slider) as typeof Slider;

// ── Component ──────────────────────────────────────────────────────────────────

const EditorTimeline = forwardRef<EditorTimelineHandle, EditorTimelineProps>((props, ref) => {
	const dispatch = useDispatch();
	const editingScene = useSelector((state: RootState) => state.scenarioReducer.editingScene);
	const timelineChanged = useSelector((state: RootState) => state.scenarioReducer.timelineChanged);
	const sceneDuration = useSelector((state: RootState) => state.scenarioReducer.editingSceneDuration);
	const defaultAccountId = useSelector((state: RootState) => state.session.defaultAccountId);
	const scenario = useSelector((state: RootState) => state.scenarioReducer.scenario);
	const { t } = useTranslation();

	const canvasRef = useRef<HTMLDivElement>(null);
	const rowsRef = useRef<HTMLDivElement>(null);
	const axisRef = useRef<HTMLDivElement>(null);
	const playheadDragRef = useRef(false);
	const movePlayerPosRef = useRef(props.movePlayerToPosition);
	const pauseRef = useRef(props.onPause);
	const playRef = useRef(props.onPlay);
	const isPlayingRef = useRef(false);
	const hasInitPaused = useRef<number>(0);
	const highestZIndexValueRef = useRef(0);
	const timelineMousePositionRef = useRef(0);

	const itemsRef = useRef<TimelineItem[]>([]);
	const viewRangeRef = useRef<ViewRange>({ startMs: 0, endMs: 10000 });
	const playheadMsRef = useRef(getTimelineStartDate().getTime());
	const sceneDurationRef = useRef(sceneDuration);
	const editingSceneRef = useRef(editingScene);
	const currentLayoutRef = useRef<Layout | null>(null);

	const dragRef = useRef<DragState>({
		active: false,
		type: null,
		item: null,
		startMouseX: 0,
		startMouseY: 0,
		startItemStartMs: 0,
		startItemEndMs: 0,
		startGroupId: null,
		currentGroupId: null,
		snapCandidates: [],
		snapThreshold: 0,
		contentWidth: 0,
		dragAxis: null,
	});

	const [items, setItems] = useState<TimelineItem[]>([]);
	const [groups, setGroups] = useState<TimelineGroup[]>([]);
	const [viewRange, setViewRange] = useState<ViewRange>({ startMs: 0, endMs: 10000 });
	const [playheadMs, setPlayheadMs] = useState(() => getTimelineStartDate().getTime());
	const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
	const [show, setShow] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isMute, setIsMuting] = useState(true);
	const [customTime, setCustomTime] = useState<Date>(() => getTimelineTimeFromTimeInput(START_TIME));
	const [zoomValue, setZoomValue] = useState(0);
	const [timeLineItemNeedToBeUpdated, setTimeLineItemNeedToBeUpdated] = useState<TimelineItem[]>([]);
	const [scrollbarWidth, setScrollbarWidth] = useState(0);
	const [snapEnabled, setSnapEnabled] = useState(true);
	const [snapIndicatorMs, setSnapIndicatorMs] = useState<number | null>(null);

	const groupsRef = useRef<TimelineGroup[]>([]);
	const snapEnabledRef = useRef(true);

	useEffect(() => { viewRangeRef.current = viewRange; }, [viewRange]);
	useEffect(() => { playheadMsRef.current = playheadMs; }, [playheadMs]);
	useEffect(() => { itemsRef.current = items; }, [items]);
	useEffect(() => { groupsRef.current = groups; }, [groups]);
	useEffect(() => { sceneDurationRef.current = sceneDuration; }, [sceneDuration]);
	useEffect(() => { editingSceneRef.current = editingScene; }, [editingScene]);
	useEffect(() => { snapEnabledRef.current = snapEnabled; }, [snapEnabled]);

	const currentLayout = useMemo<Layout>(() => {
		const layout = editingScene?.layouts?.find((l) => l.type === props.activeLayout);
		return layout ?? editingScene?.layouts?.[0] ?? ({} as Layout);
	}, [props.activeLayout, editingScene]);

	useEffect(() => { currentLayoutRef.current = currentLayout; }, [currentLayout]);

	const getContentWidth = (): number => (canvasRef.current?.clientWidth ?? 0) - LABEL_WIDTH;

	const getGroupsSorted = (groupsList: TimelineGroup[]): TimelineGroup[] =>
		[...groupsList].sort((a, b) => {
			if (a.id === 'video-group') {return 1;}
			if (b.id === 'video-group') {return -1;}
			return (Number(b.value) ?? 0) - (Number(a.value) ?? 0);
		});

	const groupIdAtY = (relY: number, sortedGs: Array<{ id: string | number }>): string | number | null => {
		if (relY < 0) {return null;}
		const idx = Math.floor(relY / ROW_HEIGHT);
		if (idx >= sortedGs.length) {return sortedGs[sortedGs.length - 1]?.id ?? null;}
		return sortedGs[idx]?.id ?? null;
	};

	const getDataRange = (): { min: number | null; max: number | null } => {
		const all = itemsRef.current;
		if (all.length === 0) {return { min: null, max: null };}
		const starts = all.map((i) => toMs(i.start));
		const ends = all.map((i) => toMs(i.end));
		return { min: Math.min(...starts), max: Math.max(...ends) };
	};

	useImperativeHandle(ref, () => ({
		updateZIndexPosition: (isBringToFront: boolean, widgetId: string, templateId: string) => {
			updateZIndexPosition(isBringToFront, widgetId, templateId);
		},
		getHighestZIndexValue: (): number => {
			if (!highestZIndexValueRef.current) {
				const boxes = currentLayoutRef.current?.boxes ?? [];
				return boxes.reduce((total, box) => total + (Array.isArray(box.widgets) ? box.widgets.length : 0), 0);
			}
			return highestZIndexValueRef.current;
		},
	}));

	const getTimelineData = (): { itemsToSet: TimelineItem[]; groups: TimelineGroup[] } => {
		const itemsToSet: TimelineItem[] = [];
		const hashedGroup: Record<string, number | string> = {};

		const boxes = (currentLayout.boxes ?? []).sort(
			(a, b) => Number(a.boxTemplateId) - Number(b.boxTemplateId)
		);
		if (!boxes || boxes.length === 0) {return { itemsToSet: [], groups: [] };}

		const sceneWidgets = editingScene?.widgetTemplates ?? [];
		if (sceneWidgets.length === 0) {return { itemsToSet: [], groups: [] };}

		boxes.forEach((box) => {
			const timelineItems = (box.widgets ?? [])
				.map((widget): TimelineItem | null => {
					const sceneWidget = sceneWidgets.find((w) => w.id === widget.widgetTemplateId) ?? {
						id: '',
						type: 'video',
						style: {} as WidgetStyle,
					};
					const zIndex = sceneWidget.style?.zIndex;
					const widgetType = sceneWidget?.type ?? 'video';
					const zIndexToSet = Number(zIndex ?? 0);
					if (widgetType !== 'video') {
						hashedGroup[String(zIndexToSet)] = zIndexToSet ?? 0;
					}
					return {
						id: widget.id + '-template-' + widget.widgetTemplateId,
						widgetId: widget.id,
						content: (sceneWidget as WidgetTemplate).name ?? 'Widget',
						group: widgetType === 'video' ? 'video-group' : zIndexToSet,
						className: getItemClassnames(widgetType),
						start: getTimelineTimeFromTimeInput((widget.start ?? '').replace('.', ':')),
						end: getTimelineTimeFromTimeInput((widget.end ?? '').replace('.', ':')),
						isVideoBG: widgetType === 'video',
						settings: (sceneWidget as WidgetTemplate).settings,
					};
				})
				.filter((item): item is TimelineItem => item !== null);
			itemsToSet.push(...timelineItems);
			if (timelineItems.find((i) => i.isVideoBG)) {
				hashedGroup['video-group'] = '';
			}
		});

		const groupsOut: TimelineGroup[] = Object.entries(hashedGroup).map(([key, value]) => {
			const base: TimelineGroup = { id: key, value, content: '', visible: true };
			return key === 'video-group' ? { ...base, className: 'video-group' } : base;
		});

		const validZIndex = Object.values(hashedGroup).filter((v) => v !== '') as number[];
		highestZIndexValueRef.current = validZIndex.length > 0 ? Math.max(...validZIndex) : 0;
		return { itemsToSet, groups: groupsOut };
	};

	const updateWidgetTimes = useCallback(
		(widgetWithCorrectTimes: TimelineItem[], updateFromContextMenu?: boolean) => {
			const scene = editingSceneRef.current;
			const layout = currentLayoutRef.current;
			if (!scene || !layout) {return;}

			const remainingLayouts = (scene.layouts ?? []).filter((l) => l.type !== layout.type);
			const widgetsToUpdateToApi: object[] = [];
			const widgetTemplates = scene.widgetTemplates ?? [];
			const hashedIndez: Record<string, number> = {};
			widgetTemplates.forEach(({ style: { zIndex } }) => {
				if (zIndex !== undefined && zIndex !== null) {hashedIndez[String(zIndex)] = zIndex;}
			});
			const groupOrderByZIndex = Object.values(hashedIndez).sort((a, b) => a - b);

			widgetWithCorrectTimes.forEach((widgetWithCorrectTime) => {
				const currentBox = (layout.boxes ?? []).find((box) => box.boxTemplateId === DEFAULT_BG_ID);
				const otherBoxes = (layout.boxes ?? []).filter((box) => box.boxTemplateId !== DEFAULT_BG_ID);
				const widgets = (currentBox?.widgets ?? []).filter((w) => w?.id !== widgetWithCorrectTime?.widgetId);
				let currentWidget = (currentBox?.widgets ?? []).find((w) => w.id === widgetWithCorrectTime?.widgetId);
				if (!currentWidget || !currentBox) {return;}

				const sceneWidget = (scene.widgetTemplates ?? []).find((w) => w.id === currentWidget!.widgetTemplateId);
				if (!sceneWidget) {return;}
				const sceneWidgets = (scene.widgetTemplates ?? []).filter((w) => w.id !== currentWidget!.widgetTemplateId);

				const oldWidgetIndex = Number(sceneWidget.style.zIndex);
				const { group } = widgetWithCorrectTime;

				if (timelineMousePositionRef.current < 0 && !updateFromContextMenu) {
					sceneWidget.style.zIndex = Number(groupOrderByZIndex[groupOrderByZIndex.length - 1]) + 1;
				} else if (group === 'video-group') {
					sceneWidget.style.zIndex = Number(groupOrderByZIndex[0]) - 1;
				} else {
					sceneWidget.style.zIndex = Number(group);
				}

				const startDate =
					widgetWithCorrectTime.start.getTime() < getTimelineStartDate().getTime()
						? getTimelineStartDate()
						: widgetWithCorrectTime.start;

				currentWidget = {
					...currentWidget,
					start: convertTimeToWidgetTime(startDate),
					end: convertTimeToWidgetTime(widgetWithCorrectTime.end),
				};
				layout.boxes = [...otherBoxes, { ...currentBox, widgets: [...widgets, { ...currentWidget }] }];

				dispatch(
					updateEditingSceneAndSendToPlayer(
						{ ...scene, layouts: [...remainingLayouts, layout], widgetTemplates: [...sceneWidgets, sceneWidget] }
					)
				);

				const updateBodyToApi =
					oldWidgetIndex === sceneWidget.style.zIndex
						? { start: currentWidget.start, end: currentWidget.end }
						: { start: currentWidget.start, end: currentWidget.end, style: sceneWidget.style };

				widgetsToUpdateToApi.push({
					defaultAccountId,
					scenarioId: scenario?.id,
					sceneId: scene.id,
					layoutId: layout?.id,
					boxId: currentBox.boxTemplateId,
					widgetId: currentWidget.id,
					body: updateBodyToApi,
				});
			});

			dispatch(addWidgetsToUpdateWaitingList(widgetsToUpdateToApi));
		},
		[editingScene, currentLayout, scenario, defaultAccountId]
	);

	const updateZIndexPosition = (isBringToFront: boolean, widgetId: string, templateId: string): void => {
		const item = itemsRef.current.find((i) => i.id === `${widgetId}-template-${templateId}`);
		if (!item) {return;}
		const widgetTemplates = editingSceneRef.current?.widgetTemplates ?? [];
		const hashedIndez: Record<string, number> = {};
		widgetTemplates.forEach(({ style: { zIndex } }) => {
			if (zIndex !== undefined && zIndex !== null) {hashedIndez[String(zIndex)] = zIndex;}
		});
		const groupOrderByZIndex = Object.values(hashedIndez).sort((a, b) => a - b);
		const zIndexToUpdate = isBringToFront
			? groupOrderByZIndex[groupOrderByZIndex.length - 1] + 1
			: groupOrderByZIndex[0] - 1;
		updateWidgetTimes([{ ...item, group: zIndexToUpdate }], true);
	};

	const onDropOnTimeline = useCallback(
		(dropData: Record<string, unknown>) => {
			props.onDropItem?.({ ...dropData, group: DEFAULT_BG_ID });
		},
		[props.onDropItem]
	);

	// ── Drag ──────────────────────────────────────────────────────────────────

	const handleItemMouseDown = useCallback(
		(e: React.MouseEvent, item: TimelineItem, dragType: DragState['type']) => {
			e.stopPropagation();
			e.preventDefault();

			const allItems = itemsRef.current;
			const snapCandidates: number[] = [];
			let allMin = Infinity;
			let allMax = -Infinity;
			allItems.forEach((i) => {
				const s = toMs(i.start);
				const en = toMs(i.end);
				allMin = Math.min(allMin, s, en);
				allMax = Math.max(allMax, s, en);
				if (i.id !== item.id) {snapCandidates.push(s, en);}
			});
			// playhead is read live in findSnapMs so it stays accurate if the player is running during drag

			const durationInSeconds = (allMax - allMin) / 1000;
			const snapThreshold =
				durationInSeconds <= 60
					? TIMELINE_SNAPPING_SENSITIVE_UNDER_A_MINUTES
					: TIMELINE_SNAPPING_SENSITIVE_FROM_10_MINUTES;

			dragRef.current = {
				active: true,
				type: dragType,
				item: { ...item },
				startMouseX: e.clientX,
				startMouseY: e.clientY,
				startItemStartMs: toMs(item.start),
				startItemEndMs: toMs(item.end),
				startGroupId: item.group,
				currentGroupId: item.group,
				snapCandidates,
				snapThreshold,
				contentWidth: getContentWidth(),
				dragAxis: null,
			};

			setSelectedItemId(item.id);

			if (dragType === 'move') {
				const itemInfo = item.id.split('-template-');
				props.onItemClick?.({
					id: itemInfo[0],
					widgetTemplateId: itemInfo[1],
					boxId: DEFAULT_BG_ID,
					group: item.group,
				});
			}
		},
		[props.onItemClick]
	);

	const findSnapMs = (targetMs: number): number => {
		if (!snapEnabledRef.current) { return targetMs; }
		const { snapCandidates, snapThreshold } = dragRef.current;

		// Check item-boundary snap candidates with the duration-scaled threshold
		let closest: number | null = null;
		let closestDist = Infinity;
		snapCandidates.forEach((c) => {
			const dist = Math.abs(c - targetMs);
			if (dist < closestDist) {
				closestDist = dist;
				closest = c;
			}
		});
		const itemSnap = closest !== null && closestDist <= snapThreshold ? { ms: closest, dist: closestDist } : null;

		// Check playhead live with a fixed 1 s threshold
		const playheadDist = Math.abs(playheadMsRef.current - targetMs);
		const playheadSnap = playheadDist <= 1000 ? { ms: playheadMsRef.current, dist: playheadDist } : null;

		// Return whichever snap point is closer; playhead wins on tie
		if (itemSnap && playheadSnap) {
			return playheadSnap.dist <= itemSnap.dist ? playheadSnap.ms : itemSnap.ms;
		}
		if (playheadSnap) { return playheadSnap.ms; }
		if (itemSnap) { return itemSnap.ms; }
		return targetMs;
	};

	const handlePlayheadMouseDown = useCallback((e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();
		playheadDragRef.current = true;
	}, []);

	const handleMouseMove = useCallback((e: MouseEvent) => {
		const canvasEl = canvasRef.current;
		if (canvasEl) {
			const rect = canvasEl.getBoundingClientRect();
			timelineMousePositionRef.current = e.clientY - rect.top - 40;
		}

		if (playheadDragRef.current) {
			const axisEl = axisRef.current;
			if (axisEl) {
				const rect = axisEl.getBoundingClientRect();
				const x = e.clientX - rect.left - LABEL_WIDTH;
				const w = rect.width - LABEL_WIDTH;
				const { startMs, endMs } = viewRangeRef.current;
				const ms = Math.max(startMs, Math.min(endMs, startMs + (x / w) * (endMs - startMs)));
				setPlayheadMs(ms);
				setCustomTime(new Date(ms));
			}
			return;
		}

		if (!dragRef.current.active) {return;}

		const { type, item, startMouseX, startMouseY, startItemStartMs, startItemEndMs, contentWidth } = dragRef.current;
		if (!item || contentWidth === 0) {return;}

		const { startMs, endMs } = viewRangeRef.current;
		const deltaMs = ((e.clientX - startMouseX) / contentWidth) * (endMs - startMs);

		let newStartMs = startItemStartMs;
		let newEndMs = startItemEndMs;
		let currentGroupId: string | number = dragRef.current.startGroupId ?? item.group;
		let snapMs: number | null = null;

		if (type === 'move') {
			// Lock drag axis once mouse has moved more than 4px
			let axis = dragRef.current.dragAxis;
			if (axis === null) {
				const absX = Math.abs(e.clientX - startMouseX);
				const absY = Math.abs(e.clientY - startMouseY);
				if (absX > 4 || absY > 4) {
					axis = absX >= absY ? 'horizontal' : 'vertical';
					dragRef.current.dragAxis = axis;
				}
			}

			if (axis === 'vertical') {
				// Keep times fixed; only change row/group.
				// Use the rows element's own rect + scrollTop so the mapping is
				// always accurate regardless of axis height or scroll position.
				const rowsEl = rowsRef.current;
				if (rowsEl) {
					const rowsRect = rowsEl.getBoundingClientRect();
					const relY = e.clientY - rowsRect.top + rowsEl.scrollTop;
					// Use the stable groups ref so the original group is always
					// present even when the dragged item was its sole occupant.
					const sortedGs = getGroupsSorted(groupsRef.current);
					const gId = groupIdAtY(relY, sortedGs);
					if (gId !== undefined && gId !== null) {currentGroupId = gId;}
				}
			} else {
				// Keep group fixed; only change times
				const duration = startItemEndMs - startItemStartMs;
				const rawStartMs = startItemStartMs + deltaMs;
				newStartMs = findSnapMs(rawStartMs);
				newEndMs = newStartMs + duration;
				snapMs = newStartMs !== rawStartMs ? newStartMs : null;
				if (snapMs === null && newEndMs === startItemEndMs + deltaMs) {
					const rawEndMs = newEndMs;
					const snappedEnd = findSnapMs(rawEndMs);
					if (snappedEnd !== rawEndMs) {
						newEndMs = snappedEnd;
						newStartMs = newEndMs - duration;
						snapMs = newEndMs;
					}
				}
				// Clamp to scene bounds while preserving item duration
				const sd = sceneDurationRef.current;
				if (sd) {
					const sdStart = sd.start ? toMs(sd.start) : null;
					const sdEnd = sd.end ? toMs(sd.end) : null;
					if (sdEnd !== null && newEndMs > sdEnd) {
						newEndMs = sdEnd;
						newStartMs = sdEnd - duration;
					}
					if (sdStart !== null && newStartMs < sdStart) {
						newStartMs = sdStart;
						newEndMs = sdStart + duration;
					}
				}
			}
		} else if (type === 'resize-left') {
			const rawStartMs = startItemStartMs + deltaMs;
			newStartMs = findSnapMs(rawStartMs);
			snapMs = newStartMs !== rawStartMs ? newStartMs : null;
		} else if (type === 'resize-right') {
			const rawEndMs = startItemEndMs + deltaMs;
			newEndMs = findSnapMs(rawEndMs);
			snapMs = newEndMs !== rawEndMs ? newEndMs : null;
		}

		if (newStartMs >= newEndMs) {return;}

		setSnapIndicatorMs(snapMs);
		dragRef.current.currentGroupId = currentGroupId;

		setItems((prev) =>
			prev.map((i) => {
				if (i.id !== item.id) {return i;}
				return { ...i, start: new Date(newStartMs), end: new Date(newEndMs), group: currentGroupId };
			})
		);
	}, []);

	const handleMouseUp = useCallback(() => {
		if (playheadDragRef.current) {
			playheadDragRef.current = false;
			const time = new Date(playheadMsRef.current);
			movePlayerPosRef.current?.(getSecondsFromTimelineTime(time));
			pauseRef.current?.();
			return;
		}

		if (!dragRef.current.active) {return;}
		dragRef.current.active = false;
		setSnapIndicatorMs(null);

		const { item, currentGroupId } = dragRef.current;
		if (!item) {return;}
		const currentItem = itemsRef.current.find((i) => i.id === item.id);
		if (!currentItem) {return;}

		if (item.isVideoBG) {
			dispatch(showAlert(`${t('SCENARIO_SCENE_EDITOR_TIMELINE_VIDEO_WIDGET_NOT_MOVE')}.`, messageTypes.info));
			setItems((prev) => prev.map((i) => (i.id === item.id ? { ...item } : i)));
			return;
		}

		let finalItem: TimelineItem = { ...currentItem, group: currentGroupId ?? currentItem.group };
		const sd = sceneDurationRef.current;
		if (sd) {
			const sdStart = sd.start ? toMs(sd.start) : null;
			const sdEnd = sd.end ? toMs(sd.end) : null;
			if (sdEnd !== null && toMs(finalItem.end) > sdEnd) {
				finalItem = { ...finalItem, end: new Date(sdEnd) };
			}
			if (sdStart !== null && toMs(finalItem.start) < sdStart) {
				finalItem = { ...finalItem, start: new Date(sdStart) };
			}
			if (toMs(finalItem.start) >= toMs(finalItem.end)) {
				setItems((prev) => prev.map((i) => (i.id === item.id ? { ...item } : i)));
				return;
			}
		}

		if (timelineMousePositionRef.current >= 0) {
			const fStart = toMs(finalItem.start);
			const fEnd = toMs(finalItem.end);
			const overlap = itemsRef.current.some((testItem) => {
				if (testItem.id === item.id) {return false;}
				if (String(finalItem.group) !== String(testItem.group)) {return false;}
				return fStart < toMs(testItem.end) && fEnd > toMs(testItem.start);
			});
			if (overlap) {
				dispatch(showAlert(t('SCENARIO_SCENE_EDITOR_TIMELINE_WIDGET_OVERLAPPED_MESSAGE'), messageTypes.info));
				setItems((prev) => prev.map((i) => (i.id === item.id ? { ...item } : i)));
				return;
			}
		}

		updateWidgetTimes([finalItem]);
	}, [updateWidgetTimes, dispatch, t]);

	// ── Axis click / drop ──────────────────────────────────────────────────────

	const handleAxisClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
		if (dragRef.current.active) {return;}
		const rect = e.currentTarget.getBoundingClientRect();
		const x = e.clientX - rect.left - LABEL_WIDTH;
		const { startMs, endMs } = viewRangeRef.current;
		const w = rect.width - LABEL_WIDTH;
		const ms = startMs + (x / w) * (endMs - startMs);
		const time = new Date(ms);
		movePlayerPosRef.current?.(getSecondsFromTimelineTime(time));
		pauseRef.current?.();
		setPlayheadMs(ms);
		setCustomTime(time);
	}, []);

	const stepPlayhead = useCallback((deltaMs: number) => {
		const sd = sceneDurationRef.current;
		let nextMs = playheadMsRef.current + deltaMs;
		const sdStart = sd?.start ? toMs(sd.start) : null;
		const sdEnd = sd?.end ? toMs(sd.end) : null;
		if (sdStart !== null) {nextMs = Math.max(sdStart, nextMs);}
		if (sdEnd !== null) {nextMs = Math.min(sdEnd, nextMs);}

		const time = new Date(nextMs);
		movePlayerPosRef.current?.(getSecondsFromTimelineTime(time));
		pauseRef.current?.();
		setPlayheadMs(nextMs);
		setCustomTime(time);
	}, []);

	const handleDrop = useCallback(
		(e: React.DragEvent<HTMLDivElement>) => {
			e.preventDefault();
			const rect = canvasRef.current?.getBoundingClientRect();
			if (!rect) {return;}
			const x = e.clientX - rect.left - LABEL_WIDTH;
			const { startMs, endMs } = viewRangeRef.current;
			const w = rect.width - LABEL_WIDTH;
			const ms = startMs + (x / w) * (endMs - startMs);
			let itemData: Record<string, unknown> = {};
			try {
				itemData = JSON.parse(e.dataTransfer.getData('text'));
			} catch (_) {}
			onDropOnTimeline({ ...itemData, time: new Date(ms) });
		},
		[onDropOnTimeline]
	);

	// ── Player messages ────────────────────────────────────────────────────────

	const handleOnMessage = useCallback((event: MessageEvent) => {
		if (event.data.identifier !== QPLAYER_IDENTIFIER || !isEventMessageComeFromEditorPlayer(event)) {return;}

		if (event.data?.type === 'ready') {
			!hasInitPaused.current && props.onPlay?.();
		}

		if (event.data?.type === 'position' && !dragRef.current.active) {
			const position: number | undefined = event.data?.metadata?.position;
			if (position !== undefined && position !== null) {
				const time = getTimelineTimeFromSeconds(position);
				const ms = toMs(time);
				setPlayheadMs(ms);
				setCustomTime(new Date(ms));
			}
		}
		if (event.data?.type === 'pause') {
			setIsPlaying(false);
		}
		if (event.data?.type === 'play') {
			if (hasInitPaused.current !== 2) {
				hasInitPaused.current++;
				props.onPause?.()
				return;
			}
			setIsPlaying(true);
		}
		if (event.data?.type === 'volume') {setIsMuting(event.data?.metadata?.volume === 0);}
	}, [props.onPause, props.onPlay]);

	// ── Zoom / fit ─────────────────────────────────────────────────────────────

	const onZoomIn = useCallback(() => {
		if (zoomValue >= 200) {return;}
		setViewRange((prev) => {
			const center = (prev.startMs + prev.endMs) / 2;
			const half = ((prev.endMs - prev.startMs) / 2) * 0.7;
			return { startMs: center - half, endMs: center + half };
		});
		setZoomValue((v) => Math.min(200, v + 20));
	}, [zoomValue]);

	const onZoomOut = useCallback(() => {
		if (zoomValue <= -200) {return;}
		setViewRange((prev) => {
			const center = (prev.startMs + prev.endMs) / 2;
			const half = ((prev.endMs - prev.startMs) / 2) / 0.7;
			return { startMs: center - half, endMs: center + half };
		});
		setZoomValue((v) => Math.max(-200, v - 20));
	}, [zoomValue]);

	const onZoomSliderChange = useCallback(
		(_event: React.ChangeEvent<{}>, value: number | number[]) => {
			const next = Array.isArray(value) ? value[0] : value;
			if (next === zoomValue) {return;}
			const zoomingIn = next > zoomValue;
			setViewRange((prev) => {
				const center = (prev.startMs + prev.endMs) / 2;
				const factor = Math.abs(next) / 100;
				const half = (prev.endMs - prev.startMs) / 2;
				const newHalf = zoomingIn ? half * (1 - factor) : half * (1 + factor);
				return { startMs: center - newHalf, endMs: center + newHalf };
			});
			setZoomValue(next);
		},
		[zoomValue]
	);

	const onFit = useCallback(() => {
		const { min, max } = getDataRange();
		if (min !== null && max !== null) {
			const padding = (max - min) * 0.05;
			setViewRange({ startMs: min - padding, endMs: max + padding });
		}
	}, []);

	const onPlayBack = (): void => (isPlaying ? props.onPause?.() : props.onPlay?.());
	const onUpdateVolume = (): void => (isMute ? props.onUnMute?.() : props.onMute?.());
	const onForward = (): void => props.onForward?.();
	const onReward = (): void => props.onReward?.();
	const handleSnapToggle = (): void => { setSnapEnabled((v) => !v); };

	// ── Data init ──────────────────────────────────────────────────────────────

	useEffect(() => {
		let request: ReturnType<typeof setTimeout> | null = null;
		const { accountId, sceneEditor } = props;
		const { itemsToSet, groups: groupsToSet } = getTimelineData();

		let timelineStartMs = toMs(getTimelineTimeFromTimeInput(START_TIME));
		const sortedByEnd = [...itemsToSet].sort((a, b) => toMs(a.end) - toMs(b.end));
		let timelineEndMs =
			sortedByEnd.length > 0 && moment(sortedByEnd[sortedByEnd.length - 1].end).isValid()
				? toMs(sortedByEnd[sortedByEnd.length - 1].end)
				: timelineStartMs + 10000;

		const BGVideo = itemsToSet.find((i) => i.isVideoBG);
		if (BGVideo) {
			const vidDuration = getWidgetsDuration({ end: BGVideo.end, start: BGVideo.start });
			if (!vidDuration) {
				const mediaId = (BGVideo.settings as { mediaId?: string })?.mediaId;
				if (mediaId) {
					if (request) {clearTimeout(request);}
					request = setTimeout(() => {
						loadResourcesAfterTheFlowStartedForMedias(accountId, mediaId).then((mediaDetail: unknown) => {
							const scene = sceneEditor?.props.editingScene;
							const tpl = scene?.widgetTemplates.find(
								({ type, settings }) =>
									type === 'video' && (settings as { mediaId?: string })?.mediaId === mediaId
							);
							const boxWidget = scene?.layouts[0].boxes[0].widgets.find(
								({ widgetTemplateId }) => widgetTemplateId === tpl?.id
							);
							sceneEditor?.updateMediaToVideoWidget(mediaDetail, tpl, boxWidget);
						});
					}, 500);
				}
			} else {
				const bgStartMs = toMs(BGVideo.start);
				const bgEndMs = bgStartMs + vidDuration;
				timelineStartMs = bgStartMs;
				timelineEndMs = bgEndMs;
				const { start, end } = sceneDuration ?? {};
				const sdStart = start ? toMs(start) : null;
				const sdEnd = end ? toMs(end) : null;
				if (!sdStart || !sdEnd || sdStart !== timelineStartMs || sdEnd !== timelineEndMs) {
					dispatch(
						updateEditingSceneDurationAction({ start: new Date(timelineStartMs), end: new Date(timelineEndMs) })
					);
				}
			}
		} else {
			dispatch(
				updateEditingSceneDurationAction({ start: new Date(timelineStartMs), end: new Date(timelineEndMs) })
			);
		}

		const itemToUpdate: TimelineItem[] = [];
		const finalItems = itemsToSet.map((i) => {
			const dur = getWidgetsDuration({ start: i.start, end: i.end });
			if (i.isVideoBG) {
				return { ...i, start: new Date(timelineStartMs), end: new Date(timelineStartMs + dur) };
			}
			const iStart = toMs(i.start);
			const iEnd = toMs(i.end);
			let next = i;
			if (iStart < timelineStartMs && iEnd > timelineEndMs) {
				next = { ...i, start: new Date(timelineStartMs), end: new Date(timelineEndMs) };
			} else if (iStart < timelineStartMs) {
				next = { ...i, start: new Date(timelineStartMs), end: new Date(timelineStartMs + dur) };
			} else if (iEnd > timelineEndMs) {
				next = { ...i, start: new Date(timelineEndMs - dur), end: new Date(timelineEndMs) };
			}
			if (next !== i) {itemToUpdate.push(next);}
			return next;
		});

		setTimeout(() => {
			setItems(finalItems);
			setGroups(groupsToSet);
			setViewRange({ startMs: timelineStartMs, endMs: timelineEndMs });
			setShow(true);
		});

		if (itemToUpdate.length > 0) {setTimeLineItemNeedToBeUpdated(itemToUpdate);}

		return () => { if (request) {clearTimeout(request);} };
	}, [currentLayout, timelineChanged, editingScene]);

	useEffect(() => { movePlayerPosRef.current = props.movePlayerToPosition; }, [props.movePlayerToPosition]);
	useEffect(() => { pauseRef.current = props.onPause; }, [props.onPause]);
	useEffect(() => { playRef.current = props.onPlay; }, [props.onPlay]);
	useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);

	useEffect(() => {
		if (props.editingWidget) {
			setSelectedItemId(`${props.editingWidget.id}-template-${props.editingWidget.widgetTemplateId}`);
		}
	}, [props.editingWidget]);

	useEffect(() => {
		const getSceneDuration = (): number =>
			getWidgetsDuration({ end: sceneDuration?.end ?? new Date(0), start: sceneDuration?.start ?? new Date(0) });
		props.getSceneDuration?.(getSceneDuration);
	}, [props.getSceneDuration]);

	useEffect(() => {
		const time = getTimelineTimeFromSeconds(0);
		setPlayheadMs(toMs(time));
		setCustomTime(new Date(toMs(time)));
	}, []);

	useEffect(() => {
		if (timeLineItemNeedToBeUpdated.length === 0) {return;}
		setTimeLineItemNeedToBeUpdated([]);
		updateWidgetTimes(timeLineItemNeedToBeUpdated);
	}, [timeLineItemNeedToBeUpdated]);

	useEffect(() => {
		const el = rowsRef.current;
		if (!el) { return; }
		const measure = () => setScrollbarWidth(el.offsetWidth - el.clientWidth);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		window.addEventListener('message', handleOnMessage);
		return () => window.removeEventListener('message', handleOnMessage);
	}, [handleOnMessage]);

	useEffect(() => {
		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);
		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [handleMouseMove, handleMouseUp]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent): void => {
			if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== ' ') {return;}
			if (dragRef.current.active || playheadDragRef.current) {return;}

			const target = e.target as HTMLElement | null;
			const tag = target?.tagName;
			if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {return;}

			e.preventDefault();
			if (e.key === ' ') {
				isPlayingRef.current ? pauseRef.current?.() : playRef.current?.();
				return;
			}
			stepPlayhead(e.key === 'ArrowRight' ? 1000 : -1000);
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [stepPlayhead]);

	// ── Derived ────────────────────────────────────────────────────────────────

	const sortedGroups = useMemo(() => getGroupsSorted(groups), [groups]);

	const ticks = useMemo(() => generateTicks(viewRange.startMs, viewRange.endMs), [viewRange]);

	const playheadPercent = useMemo(() => {
		const { startMs, endMs } = viewRange;
		if (endMs === startMs) {return 0;}
		return ((playheadMs - startMs) / (endMs - startMs)) * 100;
	}, [playheadMs, viewRange]);

	const snapIndicatorPercent = useMemo(() => {
		if (snapIndicatorMs === null) { return null; }
		const { startMs, endMs } = viewRange;
		if (endMs === startMs) { return null; }
		return ((snapIndicatorMs - startMs) / (endMs - startMs)) * 100;
	}, [snapIndicatorMs, viewRange]);

	const playheadLabelTime = moment(customTime).isValid()
		? moment(customTime).format('HH:mm:ss.SSS')
		: '';

	// ── Render ─────────────────────────────────────────────────────────────────

	return (
		<>
			<div className={`${baseClassName}__toolbar`} style={{ visibility: show ? 'visible' : 'hidden' }}>
				<div className={`${baseClassName}__toolbar-slot`} />
				<div className={`${baseClassName}__toolbar-slot`}>
					<div className={`${baseClassName}__toolbar--timestamp`}>
						{playheadLabelTime}
					</div>
					<IconButton
						className={`${baseClassName}__toolbar--btn`}
						title={t('SCENARIO_SCENE_EDITOR_TIMELINE_REWARD')}
						onClick={onReward}
					>
						<img src={fast_reward_15} alt="" />
					</IconButton>
					<IconButton
						className={`${baseClassName}__toolbar--btn`}
						title={t('SCENARIO_SCENE_EDITOR_TIMELINE_PLAYBACK')}
						onClick={onPlayBack}
					>
						<img src={isPlaying ? pause_controls : play_black} alt="" />
					</IconButton>
					<IconButton
						className={`${baseClassName}__toolbar--btn`}
						title={t('SCENARIO_SCENE_EDITOR_TIMELINE_FORWARD')}
						onClick={onForward}
					>
						<img src={fast_forward_15} alt="" />
					</IconButton>
					<IconButton className={`${baseClassName}__toolbar--btn`} onClick={onUpdateVolume}>
						{isMute ? <VolumeOffIcon htmlColor="#000" /> : <VolumeUpIcon htmlColor="#000" />}
					</IconButton>
				</div>
				<div className={`${baseClassName}__toolbar-slot ${baseClassName}__toolbar-slot--middle`}>
					<IconButton
						className={`${baseClassName}__toolbar--btn${snapEnabled ? ` ${baseClassName}__toolbar--btn-snap-active` : ''}`}
						title={t('SCENARIO_SCENE_EDITOR_TIMELINE_SNAP')}
						onClick={handleSnapToggle}
					>
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M9.99992 14.7834C11.3806 14.7834 12.4999 12.6418 12.4999 10C12.4999 7.35826 11.3806 5.21669 9.99992 5.21669C8.61921 5.21669 7.49992 7.35826 7.49992 10C7.49992 12.6418 8.61921 14.7834 9.99992 14.7834Z" fill="#1F2937"/>
							<path d="M7.53325 14.0584C7.25825 14.125 6.96659 14.1667 6.66659 14.1667C4.36659 14.1667 2.49992 12.3 2.49992 10C2.49992 7.70002 4.36659 5.83335 6.66659 5.83335C6.96659 5.83335 7.25825 5.87502 7.53325 5.94169C7.85825 5.47502 8.26659 5.00835 8.77492 4.58335C8.12492 4.32502 7.41659 4.16669 6.66659 4.16669C3.44992 4.16669 0.833252 6.78335 0.833252 10C0.833252 13.2167 3.44992 15.8334 6.66659 15.8334C7.41659 15.8334 8.12492 15.675 8.77492 15.4167C8.26659 14.9917 7.85825 14.525 7.53325 14.0584Z" fill="#1F2937"/>
							<path d="M13.3333 4.16669C12.5833 4.16669 11.8749 4.32502 11.2249 4.58335C11.7333 5.00835 12.1416 5.47502 12.4666 5.94169C12.7416 5.87502 13.0333 5.83335 13.3333 5.83335C15.6333 5.83335 17.4999 7.70002 17.4999 10C17.4999 12.3 15.6333 14.1667 13.3333 14.1667C13.0333 14.1667 12.7416 14.125 12.4666 14.0584C12.1416 14.525 11.7333 14.9917 11.2249 15.4167C11.8749 15.675 12.5833 15.8334 13.3333 15.8334C16.5499 15.8334 19.1666 13.2167 19.1666 10C19.1666 6.78335 16.5499 4.16669 13.3333 4.16669Z" fill="#1F2937"/>
						</svg>
					</IconButton>
				</div>
				<div className={`${baseClassName}__toolbar-slot`}>
					<IconButton
						className={`${baseClassName}__toolbar--btn ${baseClassName}__toolbar--btnZoom`}
						onClick={onZoomOut}
						title={t('SCENARIO_SCENE_EDITOR_TIMELINE_ZOOM_OUT')}
					>
						<ZoomOut />
					</IconButton>
					<CustomSlider
						max={200}
						min={-200}
						step={10}
						className={`${baseClassName}__toolbar--slider`}
						value={zoomValue}
						valueLabelDisplay={'off'}
						onChange={onZoomSliderChange}
					/>
					<IconButton
						className={`${baseClassName}__toolbar--btn ${baseClassName}__toolbar--btnZoom`}
						onClick={onZoomIn}
						title={t('SCENARIO_SCENE_EDITOR_TIMELINE_ZOOM_IN')}
					>
						<ZoomIn />
					</IconButton>
					<Button
						className={`${baseClassName}__toolbar--btn ${baseClassName}__toolbar--btnFit`}
						variant="outlined"
						onClick={onFit}
					>
						{t('SCENARIO_SCENE_EDITOR_TIMELINE_FIT')}
					</Button>
				</div>
			</div>

			<div
				ref={canvasRef}
				className={`${baseClassName}__canvas`}
				onDragOver={(e) => e.preventDefault()}
				onDrop={handleDrop}
			>
				{/* Time axis */}
				<div ref={axisRef} className={`${baseClassName}__axis`} onClick={handleAxisClick}>
					<div className={`${baseClassName}__axis-spacer`} style={{ width: LABEL_WIDTH }} />
					<div className={`${baseClassName}__axis-ticks`} style={{ marginRight: scrollbarWidth }}>
						{ticks.map((ms) => (
							<div
								key={ms}
								className={`${baseClassName}__tick`}
								style={{
									left: `${((ms - viewRange.startMs) / (viewRange.endMs - viewRange.startMs)) * 100}%`,
								}}
							>
								<span className={`${baseClassName}__tick-label`}>{formatMs(ms)}</span>
							</div>
						))}
						<div
							className={`${baseClassName}__playhead-head`}
							style={{ left: `${playheadPercent}%` }}
							onMouseDown={handlePlayheadMouseDown}
						>
							<span
								className={`${baseClassName}__playhead-label`}
								style={playheadDragRef.current ? { display: 'block' } : { display: 'none' }}
							>
								{playheadLabelTime}
							</span>
						</div>
					</div>
				</div>

				{/* Rows */}
				<div ref={rowsRef} className={`${baseClassName}__rows`}>
					{sortedGroups.map((group) => {
						const groupItems = items.filter((i) => String(i.group) === String(group.id));
						return (
							<div
								key={group.id}
								className={`${baseClassName}__row${group.className ? ' ' + group.className : ''}`}
							>
								<div className={`${baseClassName}__row-label`} style={{ width: LABEL_WIDTH }} />
								<div className={`${baseClassName}__row-content`}>
									{groupItems.map((item) => {
										const iStartMs = toMs(item.start);
										const iEndMs = toMs(item.end);
										const { startMs, endMs } = viewRange;
										const leftPct = ((iStartMs - startMs) / (endMs - startMs)) * 100;
										const widthPct = ((iEndMs - iStartMs) / (endMs - startMs)) * 100;
										const isSelected = selectedItemId === item.id;

										return (
											<div
												key={item.id}
												className={`${item.className}${isSelected ? ' selected' : ''}`}
												style={{
													position: 'absolute',
													left: `${leftPct}%`,
													width: `${Math.max(widthPct, 0.2)}%`,
													top: 4,
													bottom: 4,
												}}
												onMouseDown={(e) => handleItemMouseDown(e, item, 'move')}
											>
												<div className={`${baseClassName}__item-content`}>
													{!item.isVideoBG && (
														<div
															className={`${baseClassName}__item-handle ${baseClassName}__item-handle--left`}
															onMouseDown={(e) => {
																e.stopPropagation();
																handleItemMouseDown(e, item, 'resize-left');
															}}
														/>
													)}
													<div className={`${baseClassName}__item-content-inner`}>{item.content}</div>
													{!item.isVideoBG && (
														<div
															className={`${baseClassName}__item-handle ${baseClassName}__item-handle--right`}
															onMouseDown={(e) => {
																e.stopPropagation();
																handleItemMouseDown(e, item, 'resize-right');
															}}
														/>
													)}
												</div>
											</div>
										);
									})}
									<div
										className={`${baseClassName}__playhead`}
										style={{ left: `${playheadPercent}%` }}
									/>
									{snapIndicatorPercent !== null && (
										<div
											className={`${baseClassName}__snap-indicator`}
											style={{ left: `${snapIndicatorPercent}%` }}
										/>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</>
	);
});

export default EditorTimeline;
